import type {
  AddressInfo,
  Coordinates,
  MapAddressSuggestion,
} from "features/dt-basic-setup/single-building/model/types";

import { geocodeAddressWithFallback, reverseGeocode } from "features/dt-basic-setup/single-building/api/geocoding";
import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { COORDS_CLOSE_EPSILON } from "features/dt-basic-setup/single-building/model/constants";
import { type ChangeEvent, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { areCoordsClose } from "utils/dataManipulation";

import { buildAddressInfoFromGeocoded, buildGeocodingQueryCandidates, createAddressQuery } from "../lib/addressHelpers";

export const useAddressStep = () => {
  const { t } = useTranslation();
  const { callForRecenter, formData, projectId, recenterRequestFlag, setAddress, setMapCenter } =
    useContext(SingleBuildingContext);
  const { addressInfo, buildingCenter } = formData;

  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);

  const setMapCenterAndRefresh = useCallback(
    (geocoded: Coordinates) => {
      setMapCenter(geocoded);
      callForRecenter();
    },
    [setMapCenter, callForRecenter]
  );

  const handleInputChangeEditable = useCallback(
    (field: keyof AddressInfo) => (event: ChangeEvent<HTMLInputElement>) => {
      setAddress({ [field]: event.target.value });
    },
    [setAddress]
  );

  const handleMapMoveEditable = useCallback(
    (coords: Coordinates) => {
      if (areCoordsClose(coords, buildingCenter, COORDS_CLOSE_EPSILON)) return;
      setMapCenter(coords);
    },
    [buildingCenter, setMapCenter]
  );

  const fetchAddressFromLocation = useCallback(async () => {
    setIsFetchingAddress(true);

    try {
      const address = await reverseGeocode(buildingCenter);
      if (!address) {
        toast.error(t("AddBuilding.addressStep.geocode.addressError"));
        return;
      }

      setAddress(buildAddressInfoFromGeocoded(address));
      toast.success(t("AddBuilding.addressStep.geocode.addressSuccess"));
    } finally {
      setIsFetchingAddress(false);
    }
  }, [buildingCenter, setAddress, t]);

  const fetchLocationFromAddress = useCallback(async () => {
    const addressQuery = createAddressQuery(addressInfo);
    if (!addressQuery) {
      toast.error(t("AddBuilding.addressStep.geocode.emptyAddressError"));
      return;
    }

    setIsFetchingLocation(true);

    try {
      const geocoded = await geocodeAddressWithFallback(buildGeocodingQueryCandidates(addressInfo));
      if (!geocoded) {
        toast.error(t("AddBuilding.addressStep.geocode.locationError"));
        return;
      }

      setMapCenterAndRefresh(geocoded);
      toast.success(t("AddBuilding.addressStep.geocode.locationSuccess"));
    } finally {
      setIsFetchingLocation(false);
    }
  }, [addressInfo, setMapCenterAndRefresh, t]);

  const handlePickAddressSuggestionEditable = useCallback(
    async (suggestion: MapAddressSuggestion) => {
      const coords = { lat: suggestion.lat, lng: suggestion.lng };
      setMapCenter(coords);
      callForRecenter();

      const address = await reverseGeocode(coords);
      if (!address) {
        toast.error(t("AddBuilding.addressStep.geocode.addressError"));
        return;
      }

      setAddress(buildAddressInfoFromGeocoded(address));
      toast.success(t("AddBuilding.addressStep.geocode.addressSuccess"));
    },
    [callForRecenter, setAddress, setMapCenter, t]
  );

  const readOnlyStrategy = useMemo(
    () => ({
      fetchAddressFromLocation: async () => {},
      fetchLocationFromAddress: async () => {},
      handleInputChange: (_field: keyof AddressInfo) => (_event: ChangeEvent<HTMLInputElement>) => {},
      handleMapMove: (_coords: Coordinates) => {},
      handlePickAddressSuggestion: (_suggestion: MapAddressSuggestion) => {},
      isProjectLocked: true,
      zoomLevel: 20,
    }),
    []
  );

  const editableStrategy = useMemo(
    () => ({
      fetchAddressFromLocation,
      fetchLocationFromAddress,
      handleInputChange: handleInputChangeEditable,
      handleMapMove: handleMapMoveEditable,
      handlePickAddressSuggestion: handlePickAddressSuggestionEditable,
      isProjectLocked: false,
      zoomLevel: 14,
    }),
    [
      fetchAddressFromLocation,
      fetchLocationFromAddress,
      handleInputChangeEditable,
      handleMapMoveEditable,
      handlePickAddressSuggestionEditable,
    ]
  );

  const strategy = projectId ? readOnlyStrategy : editableStrategy;

  return {
    addressInfo,
    fetchAddressFromLocation: strategy.fetchAddressFromLocation,
    fetchLocationFromAddress: strategy.fetchLocationFromAddress,
    handleInputChange: strategy.handleInputChange,
    handleMapMove: strategy.handleMapMove,
    handlePickAddressSuggestion: strategy.handlePickAddressSuggestion,
    isEpcExtractionAvailable: true,
    isFetchingAddress,
    isFetchingLocation,
    isProjectLocked: strategy.isProjectLocked,
    mapCenter: buildingCenter,
    recenterRequestCounter: recenterRequestFlag,
    zoomLevel: strategy.zoomLevel,
  };
};
